const API_BASE = import.meta.env.VITE_API_BASE;
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import '../styles/Auth.css';

function WorkspaceSelector() {
  const navigate = useNavigate();
  const { user, workspace, listWorkspaces, createWorkspace, switchWorkspace, deleteWorkspace, renameWorkspace, apiFetch, logout } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Sharing state
  const [sharingWs, setSharingWs] = useState(null);       // workspace being shared
  const [wsMembers, setWsMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareRole, setShareRole] = useState('editor');
  const [shareMsg, setShareMsg] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listWorkspaces();
      setWorkspaces(list);
    } catch (err) {
      if (err.message === 'SESSION_EXPIRED') {
        // setAuth(null) already called in AuthContext — App will redirect to Login
        return;
      }
      setError('Could not load workspaces');
    }
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setError('');
    try {
      const newWs = await createWorkspace(newName.trim());
      setNewName('');
      setCreating(false);
      // Auto-switch token to new workspace so all subsequent API calls
      // (including generation) are scoped to it, not the previous/default workspace
      await switchWorkspace(newWs.id);
      navigate('/');
    } catch (err) { setError(err.message); }
  };

  const handleSelect = async (ws) => {
    try {
      await switchWorkspace(ws.id);
      navigate('/');
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (ws) => {
    if (!window.confirm(`Delete workspace "${ws.name}"? This cannot be undone.`)) return;
    try {
      await deleteWorkspace(ws.id);
      await load();
    } catch (err) { setError(err.message); }
  };

  const startRename = (ws) => {
    setRenamingId(ws.id);
    setRenameValue(ws.name);
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!renameValue.trim()) return;
    setError('');
    try {
      await renameWorkspace(renamingId, renameValue.trim());
      setRenamingId(null);
      setRenameValue('');
      await load();
    } catch (err) { setError(err.message); }
  };

  /* ── Sharing helpers ── */
  const openSharing = async (ws) => {
    setSharingWs(ws);
    setShareMsg('');
    setShareEmail('');
    setMembersLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/auth/workspaces/${ws.id}/members`);
      if (res.ok) { const d = await res.json(); setWsMembers(d.members || []); }
    } catch {}
    setMembersLoading(false);
  };

  const handleShareAccess = async (e) => {
    e.preventDefault();
    if (!shareEmail.trim() || !sharingWs) return;
    setShareMsg('');
    try {
      const res = await apiFetch(`${API_BASE}/auth/workspaces/${sharingWs.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: shareEmail.trim(), role: shareRole }),
      });
      if (res.ok) {
        setShareMsg(`Access granted to ${shareEmail.trim()}`);
        setShareEmail('');
        openSharing(sharingWs); // refresh members
      } else {
        const err = await res.json().catch(() => ({}));
        setShareMsg(err.detail || 'Failed');
      }
    } catch { setShareMsg('Network error'); }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!sharingWs) return;
    if (!window.confirm(`Remove ${memberName} from "${sharingWs.name}"?`)) return;
    try {
      const res = await apiFetch(`${API_BASE}/auth/workspaces/${sharingWs.id}/members/${memberId}`, { method: 'DELETE' });
      if (res.ok) openSharing(sharingWs);
      else {
        const err = await res.json().catch(() => ({}));
        setShareMsg(err.detail || 'Failed');
      }
    } catch {}
  };

  const initials = (name) => {
    const parts = (name || '').split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (name || '?')[0].toUpperCase();
  };

  const DOT_COLORS = ['#6366f1','#0f766e','#b45309','#475569','#e11d48','#0284c7','#7c3aed','#059669'];

  return (
    <div className="auth-page">
      <div className="auth-dots" />

      <div className="ws-card">
        <div className="auth-brand">
          <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
            <rect y="1" width="16" height="2.4" rx="1.2" fill="#818cf8"/>
            <rect y="6.8" width="12" height="2.4" rx="1.2" fill="#6366f1"/>
            <rect y="12.6" width="8" height="2.4" rx="1.2" fill="#4f46e5"/>
          </svg>
          <span className="auth-brand-name">Synth<em>loom</em></span>
        </div>

        <h1>Choose a workspace</h1>
        <p className="auth-subtitle">
          Welcome back, <strong>{user?.display_name || user?.name || 'User'}</strong>. Select a workspace to continue, or create a new one.
        </p>

        {error && <div className="auth-error">{error}</div>}

        {loading ? (
          <div className="ws-loading">Loading workspaces…</div>
        ) : (
          <div className="ws-list">
            {workspaces.map((ws, i) => (
              <div
                key={ws.id}
                className={`ws-item ${workspace?.id === ws.id ? 'active' : ''}`}
              >
                <div className="ws-dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }}>
                  {ws.name[0].toUpperCase()}
                </div>
                {renamingId === ws.id ? (
                  <form className="ws-rename-form" onSubmit={handleRename}>
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      autoFocus
                    />
                    <button type="submit" className="ws-rename-save" title="Save">✓</button>
                    <button type="button" className="ws-rename-cancel" onClick={() => setRenamingId(null)} title="Cancel">✗</button>
                  </form>
                ) : (
                  <div className="ws-info">
                    <span className="ws-name">{ws.name}</span>
                    <span className="ws-role">{ws.role} · created {new Date(ws.created_at).toLocaleDateString()}</span>
                  </div>
                )}
                <div className="ws-actions">
                  <button className="ws-open-btn" onClick={() => handleSelect(ws)}>
                    Open
                  </button>
                  {ws.role === 'owner' && (
                    <button className="ws-action-icon" onClick={() => openSharing(ws)} title="Share / Manage Members">
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="6" cy="5" r="2.5"/><path d="M1 14c0-3 2-5 5-5s5 2 5 5"/>
                        <line x1="12" y1="5" x2="12" y2="11"/><line x1="9" y1="8" x2="15" y2="8"/>
                      </svg>
                    </button>
                  )}
                  {ws.role === 'owner' && (
                    <button className="ws-action-icon" onClick={() => startRename(ws)} title="Rename">
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z"/>
                      </svg>
                    </button>
                  )}
                  {ws.role === 'owner' && workspaces.length > 1 && (
                    <button className="ws-delete" onClick={(e) => { e.stopPropagation(); handleDelete(ws); }} title="Delete">
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                        <line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Sharing panel (inline, below workspace list) ── */}
        {sharingWs && (
          <div className="ws-share-panel">
            <div className="ws-share-header">
              <div>
                <strong>Share "{sharingWs.name}"</strong>
                <span className="ws-share-sub">Invite users by email to collaborate in this workspace.</span>
              </div>
              <button className="ws-share-close" onClick={() => setSharingWs(null)}>×</button>
            </div>

            <form className="ws-share-form" onSubmit={handleShareAccess}>
              <input
                type="email"
                placeholder="user@example.com"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                required
              />
              <select value={shareRole} onChange={(e) => setShareRole(e.target.value)}>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <button type="submit" className="ws-open-btn">Invite</button>
            </form>

            {shareMsg && <div className="ws-share-msg">{shareMsg}</div>}

            {membersLoading ? (
              <div className="ws-loading">Loading…</div>
            ) : (
              <div className="ws-members-list">
                {wsMembers.map((m, i) => (
                  <div key={m.id} className="ws-member-row">
                    <div className="ws-dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length], width: 28, height: 28, fontSize: 11 }}>
                      {initials(m.name)}
                    </div>
                    <div className="ws-info">
                      <span className="ws-name" style={{ fontSize: 13 }}>{m.name}{m.username ? ` (@${m.username})` : ''}</span>
                      <span className="ws-role">{m.email} · {m.role}</span>
                    </div>
                    {m.role !== 'owner' && (
                      <button className="ws-delete" onClick={() => handleRemoveMember(m.id, m.name)} title="Remove">
                        <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                          <line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create new workspace */}
        {creating ? (
          <form onSubmit={handleCreate} className="ws-create-form">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Workspace name"
              autoFocus
            />
            <button type="submit" className="auth-btn auth-btn-sm">Create</button>
            <button type="button" className="auth-btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
          </form>
        ) : user?.plan === 'free' && workspaces.length >= 1 ? (
          <span className="ws-create-btn ws-create-btn-disabled" style={{ opacity: 0.7, cursor: 'not-allowed', position: 'relative' }}
            title="Upgrade to Pro to create more workspaces."
            tabIndex={0}
            onMouseEnter={e => {
              const tip = e.currentTarget.querySelector('.ws-tooltip');
              if (tip) tip.style.opacity = 1;
            }}
            onMouseLeave={e => {
              const tip = e.currentTarget.querySelector('.ws-tooltip');
              if (tip) tip.style.opacity = 0;
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
              <line x1="7" y1="2" x2="7" y2="12"/><line x1="2" y1="7" x2="12" y2="7"/>
            </svg>
            New workspace
            <span className="ws-tooltip" style={{
              opacity: 0,
              transition: 'opacity 0.2s',
              pointerEvents: 'none',
              position: 'absolute',
              left: '100%',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              background: '#222',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              maxWidth: '240px',
              marginLeft: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.13)'
            }}>Upgrade to Pro to create more workspaces.</span>
          </span>
        ) : (
          <button className="ws-create-btn" onClick={() => setCreating(true)}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
              <line x1="7" y1="2" x2="7" y2="12"/><line x1="2" y1="7" x2="12" y2="7"/>
            </svg>
            New workspace
          </button>
        )}

        <div className="ws-footer">
          <span>Signed in as <strong>{user?.email}</strong></span>
          <button className="auth-btn-ghost" onClick={logout}>Sign out</button>
        </div>
      </div>

      <p className="auth-footer">
        Synthloom — synthetic data generation platform
      </p>
    </div>
  );
}

export default WorkspaceSelector;
